// @/i18n.ts
import { APP_LANGUAGES, type Language } from "@/types";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Lazy per-language bundles. Statically importing all 5 JSONs shipped ~250KB gz
// of translations in the entry chunk — 4 languages the visitor never reads.
// Vite code-splits each import() into its own chunk; only the detected
// language (+ the en fallback) is fetched.
const loaders: Record<Language, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import("./en.json"),
  es: () => import("./es.json"),
  pt: () => import("./pt.json"),
  fr: () => import("./fr.json"),
  it: () => import("./it.json"),
};

/** Resolves once the initial language (and fallback) resources are loaded.
 *  main.tsx gates the first render on this — no raw-key flash. */
export const i18nReady = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use({
    type: "backend" as const,
    init() {},
    read(
      lng: string,
      _ns: string,
      cb: (err: unknown, data: Record<string, unknown> | null) => void,
    ) {
      const load = loaders[lng as Language];
      if (!load) return cb(new Error(`Unsupported language: ${lng}`), null);
      load()
        .then((m) => cb(null, m.default))
        .catch((e) => cb(e, null));
    },
  })
  .init({
    fallbackLng: APP_LANGUAGES[0].code,
    supportedLngs: APP_LANGUAGES.map((l) => l.code),
    load: "languageOnly",
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["path", "localStorage", "navigator"],
      lookupFromPathIndex: 1,
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
  });

export default i18n;
